<template>
  <div ref="selectWrap" v-click-outside="onClickOutSite" class="relative">
    <slot name="input-wrap" v-bind:data="{dropdownValue, toggleShowMenu}">
      <div v-if="!hiddenInput"
           class="w-full border min-h-[42.39px] rounded-[8px] p-2 inline-flex  items-center bg-[#F3F4F6] form-control"
           @click.stop="toggleShowMenu">
        <p v-if="!dropdownValue || (multiple && !dropdownValue.length)" class="text-[#797979]">{{ placeholder }}</p>
        <div class="flex flex-row gap-2 flex-1 items-center flex-wrap">
          <fragment v-if="multiple">
            <div v-for="(val, valIndex) in dropdownValue"
                 :key="valIndex"
                 class="border border-gray-300 px-1 rounded justify-center min-w-[60px]">

              <span :class="[{'w-[60px] inline-block' : !getValueLabel(val)}]">{{ getValueLabel(val) }}</span>
              <i class="fa-solid fa-xmark text-sm ml-2 text-gray-400" @click.stop="handleRemoveItem(val)"></i>
            </div>
          </fragment>
          <span v-else>
          {{ getValueLabel(dropdownValue) }}
        </span>
        </div>
        <!--  Right button -->
        <div class="space-x-2">
          <i v-if="showClearButton"
             class="fa-solid fa-xmark text-sm ml-2 text-gray-400"
             @click.stop="handleRemoveAllItem"></i>
          <i v-if="!alwaysShowMenu" :class="[showMenu ? 'fa-chevron-up' : 'fa-chevron-down']"
             class="fa-solid fa-chevron-down ml-2 text-gray-400"
             @click.stop="toggleShowMenu"
          />
        </div>
        <!-- End Right button -->
      </div>
    </slot>
    <!-- Menu -->
    <div v-if="showMenu"
         ref="menuRef"
         :class="absoluteClass ? 'absolute' : ''" class=" rounded-bl rounded-br border mt-[1px] p-2 bg-white z-10">
      <!--Search input-->
      <div v-if="searchable" class="relative h-[38px]">
        <div class="absolute left-2 top-2 pr-2 border-r">
          <i v-if="loading" class="fa-solid fa-spin fa-circle-notch"></i>
          <i v-else class="fa-solid fa-magnifying-glass"></i>
        </div>
        <input ref="inputSearch" v-model="keyword" class="border w-full h-full rounded bg-gray-50 pl-[45px]"/>
      </div>
      <!--End Search input-->

      <!--Options-->
      <ul :class="[alwaysShowMenu ?'h-[500px]':  {'h-[200px]': filteredOptions.length}]"
          class="mt-2  overflow-x-hidden overflow-y-auto"
          @scroll="onscroll"
      >
        <fragment v-if="filteredOptions.length">
          <slot name="option-content"
                v-bind:data="{optionsData: filteredOptions, handleClickOptionItem, checkItemIsSelected}">
            <li v-for="(op, opIndex) in filteredOptions" :key="opIndex"
                :class="[{'bg-blue-50 font-semibold hover:bg-red-300': checkItemIsSelected(op.id)}]"
                class="p-[1px] hover:bg-blue-200 cursor-pointer"
                @click="handleClickOptionItem(op)">
              {{ op.text }}
            </li>
          </slot>
        </fragment>
        <li v-else-if="loading" class="p-[1px] cursor-pointer">{{ loadingText }}</li>
        <li v-else class="p-[1px] cursor-pointer">{{ emptyOptionText }}</li>
      </ul>
      <!--End Options-->
      <!-- End Menu -->
    </div>
  </div>
</template>

<script>
import {$wait, convertAccentedLettersToUnsigned, unique} from "@/helpers/helpers";
import i18n from "@/plugins/i18n";

export const EVENT = {
  OPEN: "onOpen",
  CLOSE: "onClose",
  INPUT: "input",
  CHANGE: "change",
  SEARCH_CHANGE: "onSearchChange",
  SELECTED: "onSelected",
  DE_SELECTED: "onDeSelected",
  SCROLL_END: "onScrollEnd"
}
export default {
  name: "LSelect",
  props: {
    value: {
      type: [Array, Object, Number, String],
      default: null
    },
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => []
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    localSearch: {
      type: Boolean,
      default: true,
    },
    emptyOptionText: {
      type: String,
      default: i18n.t("No option data")
    },
    loadingText: {
      type: String,
      default: i18n.t("Loading data...")
    },
    loading: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    alwaysShowMenu: {
      type: Boolean,
      default: false,
    },
    hiddenInput: {
      type: Boolean,
      default: false,
    },
    absoluteClass:{
      type: Boolean,
      default: true,
    }
  },
  data: () => ({
    keyword: "",
    showMenu: false,
    // items: [],
    valueType: null,
    showValues: [],
    isCheckValueInput: false,
    isSetShowValueFirstTime: false,
    menuWith: 0,
  }),
  computed: {
    dropdownValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit(EVENT.INPUT, val);
        this.$emit(EVENT.CHANGE, val);
      }
    },
    showClearButton() {
      return this.clearable && !this.isEmptyValue(this.dropdownValue);
    },
    filteredOptions() {
      if (this.localSearch) {
        const q = convertAccentedLettersToUnsigned(this.keyword.toLowerCase());
        return this.options.filter(item => convertAccentedLettersToUnsigned(item.text.toLowerCase()).includes(q))
      }
      return this.options;
    },
    readOnly(id) {
      if (!this.multiple) {
        return this.checkItemIsSelected(id);
      }
      return false;
    },
  },
  watch: {
    keyword(val) {
      if (this.showMenu) {
        this.$emit(EVENT.SEARCH_CHANGE, val);
      }
    },
    async showMenu(val) {
      if (val) {
        this.setMenuWith();
        this.$emit(EVENT.OPEN);
        await $wait(0)
        this.$refs.inputSearch?.focus()
      } else {
        this.keyword = "";
        this.$emit(EVENT.CHANGE);
      }
    },
    options: {
      handler(val) {
        if (val && val.length && !this.isSetShowValueFirstTime) {
          this.setShowValues();
          this.isSetShowValueFirstTime = true;
        }
      },
      immediate: true,
    },
    dropdownValue: {
      handler() {
        // this.checkValueInput();
      },
      immediate: true,
    },

  },
  methods: {
    isEmptyValue(val) {
      if (this.multiple) {
        return !val.length;
      }
      return [null, "", 0].includes(val);
    },
    findShowValue(options = [], ids, results = []) {
      options.filter(x => {
        if (Array.isArray(ids)) {
          if (ids.includes(x.id)) {
            results.push({id: x.id, text: x.text});
          }
        } else if (x.id === ids) {
          results.push({id: x.id, text: x.text});
        }
        if (x?.children?.length) {
          results = [...results, ...this.findShowValue(x.children, ids, results)];
        }
      })
      return unique(results);
    },
    getValueLabel(id) {
      return this.showValues.find(x => x.id === id)?.text || "";
    },
    handleAddItem(item) {
      if (this.multiple) {
        this.dropdownValue.push(item.id);
      } else {
        this.dropdownValue = item.id;
      }
      //set showValues
      this.showValues.push(item);
    },
    handleRemoveItem(id) {
      this.dropdownValue = this.dropdownValue.filter(x => x !== id);
      //set showValues
      this.showValues = this.showValues.filter(x => x.id !== id);
    },
    handleRemoveAllItem() {
      let newDropdownValue;
      switch (this.valueType) {
        case "number": {
          newDropdownValue = 0;
          break;
        }
        case "string": {
          newDropdownValue = "";
          break;
        }
        case "object": {
          if (this.multiple) {
            newDropdownValue = [];
          }
          break;
        }
      }
      this.dropdownValue = newDropdownValue;
    },
    toggleShowMenu() {
      if (this.disabled) return;
      this.showMenu = this.alwaysShowMenu ? true : !this.showMenu;
    },
    checkItemIsSelected(id) {
      if (Array.isArray(this.dropdownValue)) {
        return this.dropdownValue.includes(id);
      } else {
        return this.dropdownValue === id;
      }
    },
    handleClickOptionItem(item) {
      const itemSelected = this.checkItemIsSelected(item.id);

      if (this.multiple && itemSelected) {
        // remove item
        this.handleRemoveItem(item.id);
        this.$emit(EVENT.DE_SELECTED, item)
      }

      this.handleAddItem(item);
      this.$emit(EVENT.SELECTED, item);

      if (!this.multiple && this.closeOnSelect) {
        this.showMenu = this.alwaysShowMenu ? true : false;
      }
    },
    onClickOutSite() {
      this.showMenu = this.alwaysShowMenu ? true : false;
    },
    onscroll({target: {scrollTop, clientHeight, scrollHeight}}) {
      if (scrollTop + clientHeight >= scrollHeight) {
        this.$emit(EVENT.SCROLL_END)
      }
    },
    async setShowValues() {
      if (this.multiple) {
        this.showValues = this.findShowValue(this.options, this.dropdownValue);
      } else {
        const val = this.options.find(x => x.id === this.dropdownValue);
        this.showValues = val ? [val] : [];
      }
    },
    checkValueInput() {
      if (this.isCheckValueInput) return;

      if (this.multiple) {
        if (!Array.isArray(this.dropdownValue)) {
          this.dropdownValue = [this.dropdownValue];
        }
      } else {
        if (Array.isArray(this.dropdownValue)) {
          this.dropdownValue = this.dropdownValue.length ? this.dropdownValue[0] : null;
        }
      }
      this.valueType = typeof this.dropdownValue;
      this.isCheckValueInput = true;
    },
    setMenuWith() {
      this.$nextTick(() => {
        const $wrap = this.$refs.selectWrap;
        const $menu = this.$refs.menuRef;
        if (!$menu) return;
        // $menu.style.width = $wrap.offsetWidth + "px";
        $menu.style.width = "100%";

        const showValueHeight = 45;
        const menuHeight = 300;
        if (window.innerHeight - $wrap.getBoundingClientRect().bottom < menuHeight) {
          $menu.style.bottom = showValueHeight + "px";
        } else {
          // $menu.style.top = showValueHeight + "px";
          $menu.style.top = "108%";
        }
      })
    },
  },
  created() {
    this.checkValueInput();
    if (this.alwaysShowMenu) {
      this.showMenu = true;
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
